const PERCENT_PATTERN = /^(?:0|[1-9]\d{0,2})(?:\.(\d{1,3}))?$/;
// The flat fee is entered as dollars, because that is how an operator writes
// money and a "$" in front of the field is the thing that stops "0.49" being
// read as half a cent. It is converted to whole cents here, once, by string
// arithmetic -- never by multiplying a float by 100.
const FIXED_FEE_PATTERN = /^(?:0|[1-9]\d{0,2})(?:\.(\d{1,2}))?$/;
/** Coarse sanity bound, matching the gateway and the API. */
export const MAX_FIXED_FEE_CENTS = 10000;
/** The gift amount every worked example on the pricing screens is based on. */
export const EXAMPLE_AMOUNT_CENTS = 10000;
const RATE_PATTERN = /^(0|1)\.(\d{5})$/;

export interface ParsedRate {
  percentInput: string;
  percentDisplay: string;
  rate: string;
  milliPercent: bigint;
}

export function parsePercent(percentInput: string, allowZero = false): ParsedRate | null {
  const trimmed = percentInput.trim();
  const match = PERCENT_PATTERN.exec(trimmed);
  if (!match) return null;

  const [wholePart, fractionalPart = ""] = trimmed.split(".");
  const whole = BigInt(wholePart);
  const fraction = BigInt(fractionalPart.padEnd(3, "0"));
  const milliPercent = whole * 1000n + fraction;
  if ((!allowZero && milliPercent === 0n) || milliPercent > 100000n) return null;

  const rateWhole = milliPercent / 100000n;
  const rateFraction = (milliPercent % 100000n).toString().padStart(5, "0");
  const normalizedFraction = fractionalPart.replace(/0+$/, "");

  return {
    percentInput: trimmed,
    percentDisplay: normalizedFraction ? `${wholePart}.${normalizedFraction}` : wholePart,
    rate: `${rateWhole}.${rateFraction}`,
    milliPercent
  };
}

export function rateToPercent(rate: string): ParsedRate {
  const match = RATE_PATTERN.exec(rate);
  if (!match) throw new Error(`Invalid canonical commission rate: ${rate}`);
  const scaled = BigInt(match[1]) * 100000n + BigInt(match[2]);
  if (scaled > 100000n) throw new Error(`Commission rate exceeds 100%: ${rate}`);
  const whole = scaled / 1000n;
  const fraction = (scaled % 1000n).toString().padStart(3, "0").replace(/0+$/, "");
  const percent = fraction ? `${whole}.${fraction}` : whole.toString();
  return { percentInput: percent, percentDisplay: percent, rate, milliPercent: scaled };
}

export interface ParsedFixedFee {
  input: string;
  /** Always two decimal places, so the field reads back as money. */
  display: string;
  cents: number;
}

/**
 * Parse a dollar amount into whole cents, exactly.
 *
 * Rejects more than two decimal places rather than rounding them away: an
 * operator who typed a third digit meant something this control cannot express,
 * and silently discarding it is how a fee ends up one cent off what was intended.
 */
export function parseFixedFee(input: string): ParsedFixedFee | null {
  const trimmed = input.trim();
  if (!FIXED_FEE_PATTERN.test(trimmed)) return null;
  const [whole, fraction = ""] = trimmed.split(".");
  const cents = Number(whole) * 100 + Number(fraction.padEnd(2, "0"));
  if (!Number.isSafeInteger(cents) || cents < 0 || cents > MAX_FIXED_FEE_CENTS) return null;
  return { input: trimmed, display: `${whole}.${fraction.padEnd(2, "0")}`, cents };
}

/** Whole cents back to the dollar string the input field shows. */
export function fixedFeeToInput(cents: number): string {
  return (cents / 100).toFixed(2);
}

/**
 * The fee for a gift: the percentage rounded ROUND_HALF_UP to whole cents, plus
 * the flat per-card fee.
 *
 * This must agree with `money.commission_cents` on the server exactly, including
 * the order of operations. A preview that disagrees with the real charge is worse
 * than no preview at all, because it is believed.
 */
export function commissionCents(amountCents: number, milliPercent: bigint, fixedCents = 0): number {
  if (!Number.isSafeInteger(amountCents) || amountCents <= 0) {
    throw new Error("Amount must be a positive integer number of cents");
  }
  if (!Number.isSafeInteger(fixedCents) || fixedCents < 0) {
    throw new Error("Flat fee must be a non-negative integer number of cents");
  }
  const numerator = BigInt(amountCents) * milliPercent;
  const percentage = Number((numerator + 50000n) / 100000n);
  return percentage + fixedCents;
}

export function formatUsd(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD"
  }).format(cents / 100);
}

/**
 * Does this change more than double or less than halve what a sender pays?
 *
 * Compared on the **total** fee for the reference gift, not on the rate alone.
 * The rate-only version of this check could not see a flat fee going from $0.49
 * to $49.00 -- which is exactly the fat-finger the confirmation step exists to
 * catch, and the more likely typo of the two now that there are two fields.
 */
export function isLargeFeeChange(currentCents: number, nextCents: number): boolean {
  if (currentCents === 0) return nextCents > 0;
  return nextCents > currentCents * 2 || nextCents * 2 < currentCents;
}

/** A fee of nothing is a real state, and reads better than "$0.00". */
export function formatFee(cents: number): string {
  return cents === 0 ? "No Fee" : formatUsd(cents);
}

/**
 * Pricing in one line: "2.9% + $0.49 per card", or just one half when the other
 * is zero. Never renders a raw decimal rate.
 */
export function describePricing(percentDisplay: string, fixedCents: number): string {
  if (fixedCents === 0) return `${percentDisplay}%`;
  if (percentDisplay === "0") return `${formatUsd(fixedCents)} per card`;
  return `${percentDisplay}% + ${formatUsd(fixedCents)} per card`;
}
